<template>
  <div class="full-height" :class="{ 'fixed-height': overflowHidden }">
    <div
      class="fillheight page-container flex-row px-8 py-5"
      :class="{ 'filter-bar-open': isShow }"
    >
      <portal to="page-option-toolbar-action">
        <slot name="toolbar-btns"></slot>
        <portal-target name="page-option-toolbar-btns" />
        <slot v-if="hasUpload" name="upload-btn">
          <base-button
            icon
            small
            type-action="upload"
            @click="$emit('click:upload')"
          >
          </base-button>
        </slot>
        <slot v-if="hasDownload" name="download-btn">
          <base-button
            icon
            small
            type-action="download"
            @click="$emit('click:download')"
          >
          </base-button>
        </slot>
        <slot v-if="hasReload" name="reload-btn">
          <base-button
            icon
            small
            type-action="reload"
            @click="onReload()"
          ></base-button>
        </slot>
        <slot name="filter-btn">
          <base-button v-if="useSiderBar" icon small @click="isShow = !isShow">
            <v-icon>mdi-filter</v-icon>
          </base-button>
        </slot>
      </portal>
      <div class="fillheight d-flex flex-column">
        <div
          class="pa-0 flex-grow-0 pb-5"
          dense
          flat
          v-if="hasTitle || (hasAdd && !disableAdd)"
        >
          <div class="d-block full-width full-height d-flex align-center">
            <v-row
              align="center"
              class="pa-0 ma-0"
              :class="{ 'flex-nowrap': isMobile }"
            >
              <slot name="pretoolbar-btns"></slot>
              <div v-if="title || $slots.title" class="d-flex align-center">
                <slot name="title">
                  <span class=" title-page"> {{ title }}</span>
                </slot>
                <v-chip
                  v-if="pagination"
                  class="ml-2"
                  color="primary"
                  outlined
                  :title="$t('common.total')"
                  x-small
                >
                  {{ pagination.total || 0 }}
                </v-chip>
              </div>
              <slot name="apptitle-btns">
                <base-button
                  v-if="hasAdd && !disableAdd"
                  class="btn_pageoption"
                  icon
                  small
                  type-action="create"
                  @click="$emit('click:add')"
                >
                </base-button>
              </slot>

              <v-spacer></v-spacer>
              <template v-if="isMobile">
                <v-menu absolute>
                  <template v-slot:activator="{ on }">
                    <base-button class="btn_pageoption" icon small v-on="on">
                      <v-icon>mdi-dots-horizontal</v-icon>
                    </base-button>
                  </template>
                  <v-card flat tile>
                    <v-card-text>
                      <portal-target name="page-option-toolbar-action" />
                    </v-card-text>
                  </v-card>
                </v-menu>
              </template>
              <template v-else>
                <portal-target
                  class="flex-grow-0 d-flex align-center"
                  name="page-option-toolbar-action"
                />
              </template>
            </v-row>
          </div>
        </div>
        <template v-if="!showError">
          <div v-if="$slots['below-toolbar']" class="pa-0 flex-grow-0 pb-1">
            <slot name="below-toolbar" />
          </div>
          <div
            class="flex-grow-1 d-flex flex-column"
            :class="{ 'overflow-hidden': overflowHidden }"
          >
            <loading-container
              class="pa-0 flex-grow-1 full-height full-width"
              :classContainer="classContainer"
              location="page-option"
            >
              <slot></slot>
            </loading-container>
          </div>
        </template>

        <div v-else class="pa-0 flex-grow-1">
          <page-404
            v-if="error.status == 404"
            :message="error.message"
          ></page-404>
          <page-403
            v-else-if="error.status == 403"
            :message="error.message"
          ></page-403>
          <page-500 v-else :message="error.message"></page-500>
        </div>
      </div>
      <v-navigation-drawer v-if="useSiderBar" v-model="isShow" absolute right>
        <v-card class="d-flex flex-column" flat height="100%" tile width="256">
          <v-toolbar class="flex-grow-0" dense flat>
            <v-toolbar-title class="text-capitalize">
              <slot name="extend-siderbar-title"></slot>
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <base-button icon :tooltip="$t('actions.close')" @click="close">
              <v-icon>mdi-close</v-icon>
            </base-button>
          </v-toolbar>
          <v-divider></v-divider>
          <div class="overflow-hidden fill-height full-width">
            <v-card-text style="overflow-y:auto">
              <slot name="extend-siderbar" />
            </v-card-text>
          </div>
          <div class="flex-grow-0">
            <slot name="extend-siderbar-btn"></slot>
          </div>
        </v-card>
      </v-navigation-drawer>
    </div>
  </div>
</template>

<script>
import { asyncComponent } from './async'
export default {
  components: {
    page404: () =>
      asyncComponent(
        import(
          /* webpackChunkName: "page-option-async" */ '@components/async/404.vue'
        )
      ),
    page403: () =>
      asyncComponent(
        import(
          /* webpackChunkName: "page-option-async" */ '@components/async/403.vue'
        )
      ),
    page500: () =>
      asyncComponent(
        import(
          /* webpackChunkName: "page-option-async" */ '@components/async/500.vue'
        )
      ),
  },
  props: {
    overflowHidden: Boolean,
    useSiderBar: Boolean,
    filters: {
      type: Array,
      default: () => [],
    },
    orderBys: {
      type: Array,
      default: () => [],
    },
    pagination: {},
    title: String,
    loading: Boolean,
    isShowSiderBar: Boolean,
    fluid: Boolean,
    error: {},
    classContainer: {
      type: String,
      default: '',
    },
    disableAdd: Boolean,
  },
  provide() {
    return {
      pageOption: {
        openSiderBar: this.openSiderBar,
      },
    }
  },
  computed: {
    hasTitle() {
      return this.title || this.$slots.title
    },
    hasReload() {
      return this.$listeners && this.$listeners['click:reload']
    },
    hasAdd() {
      return this.$listeners && this.$listeners['click:add']
    },
    hasDownload() {
      return this.$listeners && this.$listeners['click:download']
    },
    hasUpload() {
      return this.$listeners && this.$listeners['click:upload']
    },
    showError() {
      return this.error && this.error.status
    },
    isShow: {
      get() {
        return this.p_isShowSiderBar
      },
      set(value) {
        this.p_isShowSiderBar = value
        this.$emit('update:isShowSiderBar', value)
      },
    },
  },
  data: () => ({
    p_isShowSiderBar: false,
  }),
  watch: {
    isShowSiderBar: {
      handler(value) {
        this.p_isShowSiderBar = value
      },
      immediate: true,
    },
  },
  methods: {
    openSiderBar() {
      this.p_isShowSiderBar = true
    },
    toggle() {
      this.isShow = !this.isShow
    },
    close() {
      this.isShow = false
    },
    onReload() {
      this.$emit('click:reload')
    },
  },
}
</script>

<style scoped>
.page-container {
  display: flex;
  position: relative;
}
.filter-bar-open >>> .table_auth thead th {
  z-index: 0;
}
@media only screen and (min-width: 767px) {
  .fixed-height {
    height: calc(100vh - 60px - 35px);
  }
}
</style>
